/**
 * Matcher for the scoped biomarker / health-risk explorer on test pages
 * (`components/ui/biomarker-disease-explorer.tsx`, PostHog surfaces
 * `explorer-biomarkers` / `explorer-diseases`).
 *
 * It used to match a query only against the item's own-locale name,
 * description and slug, so the explorer missed spellings that global search
 * (`lib/search/engine.ts`) finds: a CMS alias ("got" for ASAT), another
 * language's name ("iron" on /nl, where the marker is "IJzer") and a one-letter
 * typo. This brings the explorer to the same reach:
 *
 * - **Every-locale names + aliases**: the cross-locale alias map, keyed by the
 *   locale-invariant doc id (`lib/search/cross-locale-aliases.ts`). It already
 *   holds the item's own-locale aliases, so one source covers both. Alias words
 *   match by **word prefix**, like global search's tokens, not anywhere inside
 *   a word: Dutch/German compounds otherwise leak ("tsh" inside
 *   "geslachtshormoon" pulled SHBG into a TSH search).
 * - **Synonyms**: the same per-locale `SYNONYMS` groups the index build expands,
 *   matched like aliases.
 * - **Typos**: a token of 4+ characters may match a name / alias / slug word
 *   within one edit (`fuzzyMatchesAny`, the global engine's rule) — but the
 *   caller tries this only when the exact pass found nothing anywhere. Fuzzy
 *   alongside exact hits adds wrong neighbours: "kalium" (potassium) is one
 *   edit from "kalzium" (calcium). Descriptions are never matched fuzzily.
 *
 * Pure: no I/O, no React.
 */

import { fuzzyMatchesAny } from "./engine"
import { normalizeForSearch } from "./normalize"
import { expandSynonyms, type SynonymGroup } from "./synonyms"

export interface ExplorerHaystack {
  /** Matched by substring anywhere: name, slug, description, extra text. */
  text: string
  /** Name / slug / alias / synonym words: matched by word prefix, and the only
   *  words a typo may match. */
  titleWords: ReadonlySet<string>
}

export interface ExplorerHaystackInput {
  name: string
  description?: string | null
  slug?: string
  /** Other names for the item: every-locale names and CMS aliases. */
  aliases?: readonly string[]
  /** Extra substring-only text (e.g. tagged symptom names). */
  extra?: readonly string[]
}

const WORD_SPLIT = /[^\p{L}\p{N}]+/u

export function buildExplorerHaystack(
  input: ExplorerHaystackInput,
  synonymGroups: ReadonlyArray<SynonymGroup>
): ExplorerHaystack {
  const slugWords = input.slug?.replace(/-/g, " ")
  const titleTerms = normalizeForSearch(
    [input.name, slugWords, ...(input.aliases ?? [])].filter(Boolean).join(" ")
  )
  const text = [
    normalizeForSearch(input.name),
    slugWords ? normalizeForSearch(slugWords) : "",
    input.description ? normalizeForSearch(input.description) : "",
    ...(input.extra ?? []).map(normalizeForSearch),
  ]
    .filter(Boolean)
    .join(" ")
  return {
    text,
    titleWords: new Set(
      expandSynonyms(titleTerms, synonymGroups)
        .split(WORD_SPLIT)
        .filter(Boolean)
    ),
  }
}

/** One normalized query token against one haystack. Exact: substring of the
 *  name / slug / description, or prefix of a name / alias / synonym word.
 *  With `typos`, also a one-edit typo of one of those words (4+ characters). */
export function explorerTokenMatches(
  haystack: ExplorerHaystack,
  token: string,
  typos: boolean
): boolean {
  if (haystack.text.includes(token)) return true
  for (const word of Array.from(haystack.titleWords)) {
    if (word.startsWith(token)) return true
  }
  return typos && fuzzyMatchesAny(token, haystack.titleWords)
}
